/* src/App.js */

import React, { useState, useContext, useRef, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Outlet, useLocation, useNavigate } from 'react-router-dom'; // Removed Link
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'; // React Query
import { scanBarcode } from './services/apiService';

// Import custom components
import UploadButton from './components/UploadButton';
import SearchResults from './components/SearchResults';
import LanguageSwitcher from './components/LanguageSwitcher';
import SettingsModal from './components/SettingsModal';
import BarcodeScanner from './components/BarcodeScanner';
import BottomNavBar from './components/BottomNavBar';
import TopNavBar from './components/TopNavBar';
import Logos from './components/Logos';
import HistoryPanel from './components/HistoryPanel';  // NEW IMPORT FOR HISTORY PANEL
import DarkModeSwitch from './components/DarkModeSwitch'; // NEW IMPORT FOR DARK MODE SWITCH
import useAlertDialog from './hooks/useAlertDialog';

// Import contexts
import { LanguageContext } from './contexts/LanguageContext';
import { SettingsContext } from './contexts/SettingsContext';

// Import custom styles
import './App.css';

// NEW: Import History icon from react-bootstrap-icons
import { ClockHistory } from 'react-bootstrap-icons';

// Initialize React Query Client with global default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,    // Prevent refetching on window focus
      refetchOnReconnect: false,      // Prevent refetching on reconnect
      refetchOnMount: false,          // Prevent refetching when component mounts
      staleTime: 1000 * 60 * 60 * 24, // Data is considered fresh for 24 hours
      cacheTime: 1000 * 60 * 60 * 24, // Cache data for 24 hours
      retry: 2,                       // Retry failed requests up to 2 times
      retryDelay: 1000,               // Wait 1 second before retrying
    },
  },
});

function App() {
  const [products, setProducts] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const { translations } = useContext(LanguageContext);
  const { settings } = useContext(SettingsContext);
  
  // Alert dialog hook
  const { showAlert } = useAlertDialog();

  const uploadButtonRef = useRef(null);
  const navigate = useNavigate(); // Initialize useNavigate
  const location = useLocation(); // Initialize useLocation

  // State to track device type
  const [isMobile, setIsMobile] = useState(false);
  
  // NEW: State to control the History Panel visibility
  const [showHistory, setShowHistory] = useState(false);

  // Function to check device type based on window width
  const checkDeviceType = () => {
    const width = window.innerWidth;
    if (width <= 767) { // Bootstrap's md breakpoint is 768px
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEffect(() => {
    // Initial check
    checkDeviceType();

    // Add event listener for window resize
    window.addEventListener('resize', checkDeviceType);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', checkDeviceType);
    };
  }, []);

  // Check for search results in sessionStorage when navigating to home
  useEffect(() => {
    if (location.pathname === '/') {
      const checkForResults = () => {
        const storedResults = sessionStorage.getItem('searchResults');
        if (storedResults) {
          try {
            const results = JSON.parse(storedResults);
            setProducts(results);
            // Clear the sessionStorage after using it
            sessionStorage.removeItem('searchResults');
          } catch (error) {
            console.error('Error parsing stored search results:', error);
            sessionStorage.removeItem('searchResults');
          }
        }
      };

      // Check immediately
      checkForResults();

      // Also check after a small delay in case of timing issues
      const timeout = setTimeout(checkForResults, 100);

      return () => clearTimeout(timeout);
    }
  }, [location.pathname]);

  // Handle Paste Events
  useEffect(() => {
    const handlePaste = (event) => {
      const items = event.clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const blob = items[i].getAsFile();
          if (blob) {
            const file = new File([blob], 'pasted_image.png', { type: blob.type });
            // Pass the file to UploadButton via ref
            if (uploadButtonRef.current) {
              uploadButtonRef.current.setImage(file);
            }
            event.preventDefault(); // Prevent the default paste behavior
            break; // Only handle the first image
          }
        }
      }
    };

    // Add paste event listener
    window.addEventListener('paste', handlePaste);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('paste', handlePaste);
    };
  }, []);

  // Refresh products auto-update with sequence guard to avoid oscillation after selecting different history entries.
  // Added staleness gating: only refresh if any product is older than STALE_AFTER_MS.
  const autoRefreshSeqRef = useRef(0);
  const STALE_AFTER_MS = 60 * 1000; // 1 minute freshness window (adjust as needed)
  useEffect(() => {
    if (location.pathname === '/' && !isMobile && products.length > 0) {
      const now = Date.now();
      const needsRefresh = products.some(p => !p.lastUpdated || (now - p.lastUpdated) > STALE_AFTER_MS);
      if (!needsRefresh) return; // Skip unnecessary rescans
      const seq = ++autoRefreshSeqRef.current;
      fetchAndUpdateProductsFromServer(products)
        .then(updatedProducts => {
          if (autoRefreshSeqRef.current !== seq) return; // stale
          const visibleProducts = updatedProducts.filter(p => !p.hidden);
          // If a history selection is pending and represents a different set, don't overwrite.
          if (latestHistorySelectionRef && latestHistorySelectionRef.current) {
            const selIds = new Set((latestHistorySelectionRef.current.products || []).map(p => p.id));
            const newIds = new Set(visibleProducts.map(p => p.id));
            let mismatch = selIds.size !== newIds.size;
            if (!mismatch) {
              for (const id of selIds) { if (!newIds.has(id)) { mismatch = true; break; } }
            }
            if (mismatch) return;
          }
          setProducts(visibleProducts);
        })
        .catch(err => {
          if (autoRefreshSeqRef.current === seq) {
            console.error('Error refreshing products from server:', err);
          }
        });
    }
  }, [location.pathname, isMobile, products]);

  /**
   * When an image upload search completes successfully, update the products
   * and navigate to the home route ("/") so that search results are shown.
   */
  const handleImageUpload = (uploadedProducts) => {
    const stamped = (uploadedProducts || []).map(p => ({ ...p, lastUpdated: Date.now() }));
    setProducts(stamped);
    // If not already on home, navigate to "/" to display search results.
    navigate("/", { replace: true });
  };

  /*
  // Login functionality is not implemented yet.
  // The following function is commented out to hide the login functionality.
  const handleLogin = () => {
    showAlert({
      title: translations.information || 'Information',
      message: translations.loginFunctionality || 'Login functionality is not implemented yet.',
      variant: 'info',
    });
  };
  */

  const openSettings = () => {
    setShowSettings(true);
  };

  const closeSettings = () => {
    setShowSettings(false);
  };

  // Barcode Scanner
  const openScanner = () => {
    setShowScanner(true);
  };

  const closeScanner = () => {
    setShowScanner(false);
  };

  const handleBarcodeDetected = async (barcode) => {
    try {
      const response = await scanBarcode(barcode); // do NOT skip log here, because it's a real scan.
      if (response.status === 200) {
        if (response.data.product) {
          setProducts([response.data.product]);
          // Optionally, prefetch related content if needed
        } else {
          showAlert({
            title: translations.information || 'Information',
            message: translations.noProductFound || 'No product found for this barcode.',
            variant: 'warning',
          });
        }
      }
    } catch (error) {
      console.error('Error scanning barcode:', error);
      showAlert({
        title: translations.error || 'Error',
        message: translations.errorScanningBarcode || 'Error occurred while scanning the barcode.',
        variant: 'danger',
      });
    }
  };

  const handleHomeClick = () => {
    // When home is clicked, navigate to "/" so that search results are shown.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle "Upload" trigger from BottomNavBar (mobile)
  const handleBottomNavUploadClick = () => {
    if (uploadButtonRef.current) {
      uploadButtonRef.current.triggerFileInput(true);
    }
  };

  // Decide if center UI (buttons) should be visible
  const showCenteredButtons =
    settings.layout === 'center_only' ||
    settings.layout === 'both' ||
    (settings.layout === 'bottom_only' && !isMobile);

  /**
   * IMPORTANT DEVELOPER NOTES (DO NOT REMOVE):
   *
   * This function (fetchAndUpdateProductsFromServer) processes the locally cached products 
   * in small slices (with a concurrency limit of 2) to update each product by re-scanning its barcode.
   * This approach prevents the app from freezing on mobile devices where processing too many 
   * concurrent API calls can lead to UI delays. It also updates a `lastUpdated` property on each 
   * product to know when the refresh occurred.
   *
   * DO NOT REMOVE OR MODIFY THESE DETAILED COMMENTS.
   */
  async function fetchAndUpdateProductsFromServer(localProducts) {
    try {
      const concurrencyLimit = 2; 
      const updated = [];
      let index = 0;

      // Process localProducts in small slices
      while (index < localProducts.length) {
        const slice = localProducts.slice(index, index + concurrencyLimit);

        const results = await Promise.all(
          slice.map(async (localProd) => {
            try {
              // skipLog=true so we don't create extra SearchLog
              const res = await scanBarcode(localProd.barcode.replace(/\//g, ""), true);
              /*
                If the server returns a product object, we use it.
                Keep both hidden and unhidden products in history records.
                The display filtering will happen in SearchResults component.
              */
              if (res.status === 200 && res.data.product) {
                // Return the product with updated timestamp regardless of hidden status
                return { ...res.data.product, lastUpdated: Date.now() };
              } else {
                // Product not found or server error => remove from local
                return null;
              }
            } catch (err) {
              console.error(`Error scanning barcode ${localProd.barcode}:`, err);
              return null;
            }
          })
        );

        const validResults = results.filter((prod) => prod !== null);
        updated.push(...validResults);
        index += concurrencyLimit;
      }
      return updated;
    } catch (err) {
      console.error('Error updating products from server:', err);
      return localProducts;
    }
  }

  // A ref-based timer to help avoid flicker or double updates
  const selectionTimerRef = useRef(null);
  // NEW: Ref to track the latest selected history item.
  const latestHistorySelectionRef = useRef(null);

  return (
    <QueryClientProvider client={queryClient}>
      <Container fluid className="py-4">
        {/* 
          Header Row: 
          Logos on the left and header controls on the right.
          The dark mode switch is positioned absolutely so it appears above the other controls.
        */}
        <Row className="justify-content-center mb-3">
          <Col xs={12} md={10} lg={8} className="position-relative d-flex justify-content-between align-items-center" style={{ minHeight: '50px' }}>
            <Logos />

            {/* Right-side controls */}
            <div className="d-flex align-items-center">
              <LanguageSwitcher />

              {/* Settings Button is hidden ONLY IF bottom_only + mobile */}
              {!(settings.layout === 'bottom_only' && isMobile) && (
                <Button
                  variant="secondary"
                  onClick={openSettings}
                  className="ms-3 d-flex align-items-center"
                  aria-label={translations.settings} // Accessibility label
                >
                  {/* Cog Icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="currentColor"
                    className="bi bi-gear"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z" />
                    <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.286.286 0 0 1-.376.19l-.292-.12c-1.64-.678-3.433.763-2.856 2.417l.094.319a.286.286 0 0 1-.19.376l-.319.094c-1.654.577-1.654 3.115 0 3.692l.319.094a.286.286 0 0 1 .19.376l-.094.319c-.578 1.654 1.216 3.095 2.856 2.417l.292-.12a.286.286 0 0 1 .376.19l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.286.286 0 0 1 .376-.19l.292.12c1.64.678 3.433-.763 2.856-2.417l-.094-.319a.286.286 0 0 1 .19-.376l.319-.094c1.654-.577 1.654-3.115 0-3.692l-.319-.094a.286.286 0 0 1-.19-.376l.094-.319c.578-1.654-1.216-3.095-2.856-2.417l-.292.12a.286.286 0 0 1-.376-.19l-.094-.319zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.286 1.286 0 0 0 1.69.86l.292-.12c.764-.317 1.6.48 1.345 1.28l-.094.319a1.286 1.286 0 0 0 .86 1.69l.319.094c.8.246.8 1.428 0 1.674l-.319.094a1.286 1.286 0 0 0-.86 1.69l.094.319c.255.8-.58 1.598-1.345 1.28l-.292-.12a1.286 1.286 0 0 0-1.69.86l-.094.319c-.246.835-1.428.835-1.674 0l-.094-.319a1.286 1.286 0 0 0-1.69-.86l-.292.12c-.764.317-1.6-.48-1.345-1.28l.094-.319a1.286 1.286 0 0 0-.86-1.69l-.319-.094c-.8-.246-.8-1.428 0-1.674l.319-.094a1.286 1.286 0 0 0 .86-1.69l-.094-.319c-.255-.8.58-1.598 1.345-1.28l.292.12a1.286 1.286 0 0 0 1.69-.86l.094-.319z" />
                  </svg>
                  {/* Settings Text - Hidden on small screens */}
                  <span className="d-none d-md-inline ms-1">
                    {translations.settings}
                  </span>
                </Button>
              )}
              {/* Dark Mode Switch */}
              <div className="ms-3">
                <DarkModeSwitch />
              </div>
              {/* History Button */}
              <Button
                variant="secondary"
                onClick={() => setShowHistory(true)}
                className="ms-3 d-flex align-items-center"
                aria-label={translations.history}
              >
                <ClockHistory size={20} className="me-1" />
                <span className="d-none d-md-inline">{translations.history}</span>
              </Button>
            </div>
          </Col>
        </Row>

        {/* TopNavBar */}
        <Row className="justify-content-center mb-3">
          <Col xs={12}>
            <TopNavBar />
          </Col>
        </Row>

        {/* Centered UploadButton if layout allows */}
        <Row className="justify-content-center mb-3">
          <Col xs="auto">
            <UploadButton
              ref={uploadButtonRef}
              onUpload={handleImageUpload}
              openScanner={openScanner}
              showUI={showCenteredButtons}
            />
          </Col>
        </Row>

        {/* Main content area: either SearchResults ("/") or child routes via Outlet */}
        {location.pathname === "/" ? (
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8}>
              <SearchResults key={JSON.stringify(products)} products={products} onUpload={handleImageUpload} />
            </Col>
          </Row>
        ) : (
          <Row className="justify-content-center mt-4">
            <Col xs={12} md={10} lg={8}>
              <Outlet />
            </Col>
          </Row>
        )}

        {/* Settings Modal */}
        <SettingsModal show={showSettings} onClose={closeSettings} />

        {/* Barcode Scanner Modal */}
        <BarcodeScanner
          show={showScanner}
          handleClose={closeScanner}
          onDetected={handleBarcodeDetected}
        />

        {/* History Panel */}
        <HistoryPanel
          show={showHistory}
          handleClose={() => setShowHistory(false)}
          onSelect={(item) => {
            setShowHistory(false);
            // Store the latest selection in our protected ref.
            latestHistorySelectionRef.current = item;

            // Cancel any previous timer so we don't set intermediate results.
            if (selectionTimerRef.current) {
              clearTimeout(selectionTimerRef.current);
            }

            // -------------------------------------------------------------------
            // BEGIN: VERY IMPORTANT HISTORY SELECTION BLOCK (DO NOT REMOVE)
            //
            // We use a 300ms delay before applying the selected history record.
            // This delay prevents UI flicker and ensures that if multiple selections
            // occur in quick succession, only the final one is applied.
            //
            // The following steps occur:
            // 1. Immediately display local (cached) product data for instant response
            // 2. Navigate to the home route ("/") so that the SearchResults component updates.
            // 3. On non-mobile devices, conditionally refresh the product data from the server if the cached
            //    data is older than 1 second. This avoids freezing mobile devices with heavy network requests.
            // 4. Update the local history record with the new data and dispatch a "historyUpdated" event.
            // 5. Only update the displayed products if the history item in our ref still matches the current selection.
            //
            // DO NOT REMOVE OR MODIFY THESE COMMENTS OR THE LOGIC BELOW.
            // -------------------------------------------------------------------
            selectionTimerRef.current = setTimeout(() => {
              // Only update if this is still the latest selection
              if (latestHistorySelectionRef.current?.id !== item.id) return;
              // Immediately display local (cached) data for instant response
              // Stamp lastUpdated so immediate auto refresh won't re-scan
              const stamped = (item.products || []).map(p => ({ ...p, lastUpdated: Date.now() }));
              setProducts(stamped);
              navigate("/", { replace: true });

              // Only refresh from server if device is not mobile (to avoid freeze)
              if (!isMobile) {
                // ALWAYS REFRESH => so that unhide changes show up
                fetchAndUpdateProductsFromServer(item.products).then((updatedProducts) => {
                  // Update local history record and dispatch an event
                  const stored = JSON.parse(localStorage.getItem('searchHistory') || '[]');
                  const updatedHistory = stored.map((record) => {
                    if (record.id === item.id) {
                      return { ...record, products: updatedProducts.map(p => ({ ...p, lastUpdated: Date.now() })) };
                    }
                    return record;
                  });
                  localStorage.setItem('searchHistory', JSON.stringify(updatedHistory));
                  window.dispatchEvent(new Event('historyUpdated'));
                  // Only update displayed products if the selection has not changed
                  if (
                    latestHistorySelectionRef.current &&
                    latestHistorySelectionRef.current.id === item.id
                  ) {
                    // Filter out hidden products for display
                    const visibleProducts = updatedProducts.filter(product => !product.hidden).map(p => ({ ...p, lastUpdated: Date.now() }));
                    setProducts(visibleProducts);
                  }
                });
              }
            }, 300);
            // END: VERY IMPORTANT HISTORY SELECTION BLOCK (DO NOT REMOVE)
          }}
        />

        {/* Bottom Navigation Bar */}
        <BottomNavBar
          onHomeClick={handleHomeClick}
          onUploadClick={handleBottomNavUploadClick}
          onSettingsClick={openSettings}
          // onLoginClick is omitted since login is hidden
          onBarcodeClick={openScanner}
        />
      </Container>

    </QueryClientProvider>
  );
}

export default App;
